import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Advanced, Badge, Btn } from "./common";
import { useI18n } from "@/lib/i18n";
import { Coins, KeyRound, Cpu, Radio, Mic, MicOff } from "lucide-react";

/* Unified "AI and voice" settings (UPDATE_EXPANSION §7).
   Main view: chat source · default model · connection status · voice choice. Technical detail (endpoints,
   billing, capabilities, key origin) lives in the collapsed Advanced block. Nothing here starts a paid
   session: status is read without contacting providers; only "Test" contacts the selected provider. */
const GROUPS = [
  ["platform", "Platform", Coins, ["emergent"]],
  ["api", "API bağlantılarım", KeyRound, ["openai", "anthropic"]],
  ["local", "AI PC'm", Cpu, ["local"]],
];
const NAMES = { emergent: "Platform (Emergent)", openai: "OpenAI", anthropic: "Claude (Anthropic)", local: "AI PC" };

const StatusBadge = ({ s }) => {
  const { tx } = useI18n();
  if (!s.configured) return <Badge color="gray">{tx("bağlı değil")}</Badge>;
  if (s.verified) return <Badge color="green">{tx("doğrulandı")}</Badge>;
  if (s.last_test) return <Badge color="red">{tx("test başarısız")}</Badge>;
  return <Badge color="amber">{tx("yapılandırıldı · test edilmedi")}</Badge>;
};

export const AiVoiceSettings = ({ onRecheck, onVoiceModeChange }) => {
  const { tx } = useI18n();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [choosing, setChoosing] = useState("");
  const [keyDraft, setKeyDraft] = useState({});
  const [endpoint, setEndpoint] = useState("");

  const load = useCallback(async () => {
    try { const r = await api.get("/ai/sources"); setS(r.data); setEndpoint(r.data?.local?.endpoint || ""); }
    catch { setS({ error: true }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const err = (e) => toast.error(tx(e?.response?.data?.detail || "Kaydedilemedi"));
  const choose = async (id) => {
    if (id === s.active || choosing) return;
    setChoosing(id);
    try { await api.put("/settings", { ai_provider: id }); await load(); onRecheck?.(); toast.success(`${tx("Sohbet kaynağı")}: ${tx(NAMES[id])}`); }
    catch (e) { err(e); } finally { setChoosing(""); }
  };
  const test = async (id) => {
    setBusy(`test-${id}`);
    try {
      const r = await api.post(`/ai/test/${id}`);
      await load(); onRecheck?.();
      (r.data?.ok ? toast.success : toast.error)(tx(r.data?.ok ? "Bağlantı doğrulandı" : (r.data?.message || "Test başarısız")));
    } catch (e) { err(e); } finally { setBusy(""); }
  };
  const saveKey = async (id) => {
    const v = (keyDraft[id] || "").trim();
    if (!v) return;
    setBusy(`key-${id}`);
    try { await api.put(`/ai/keys/${id}`, { api_key: v }); setKeyDraft((k) => ({ ...k, [id]: "" })); await load(); toast.success(tx("Anahtar sunucuda saklandı")); }
    catch (e) { err(e); } finally { setBusy(""); }
  };
  const removeKey = async (id) => {
    try { const r = await api.delete(`/ai/keys/${id}`); await load(); toast.success(tx(r.data?.note || "Anahtar kaldırıldı")); }
    catch (e) { err(e); }
  };
  const saveDefault = async (id, model) => {
    try { const r = await api.put("/ai/default-model", { provider: id, model }); setS(r.data); }
    catch (e) { err(e); }
  };
  const saveLocal = async (patch) => {
    try { const r = await api.put("/ai/local", patch); setS(r.data); toast.success(tx("Kaydedildi")); }
    catch (e) { err(e); }
  };
  const setVoice = async (mode) => {
    if (mode === s.voice.mode) return;
    try { await onVoiceModeChange?.(mode); await load(); }
    catch (e) { err(e); }
  };
  const setFallback = async (p) => {
    try { await api.put("/settings", { fallback_provider: p || null }); await load(); } catch (e) { err(e); }
  };

  if (!s) return <div className="text-xs text-white/40">{tx("Durum alınıyor…")}</div>;
  if (s.error) return <div className="text-xs text-red-300">{tx("Ayarlar yüklenemedi.")}</div>;
  const byId = Object.fromEntries(s.sources.map((x) => [x.id, x]));
  const active = byId[s.active] || s.sources.find((x) => x.configured) || s.sources[0];

  return (
    <div data-testid="ai-voice-settings" className="space-y-4">
      {/* ---------------- A. chat source ---------------- */}
      <section aria-label={tx("Sohbet zekâsı")}>
        <div className="label-eyebrow mb-2">{tx("Sohbet zekâsı")}</div>
        <div className="grid sm:grid-cols-3 gap-2.5">
          {GROUPS.map(([gid, glabel, Icon, ids]) => (
            <div key={gid} className={`rounded-xl border p-3 ${ids.includes(s.active) ? "border-cyan-400/60 bg-cyan-500/10" : "border-white/10 bg-black/20"}`} data-testid={`ai-source-${gid}`}>
              <div className="flex items-center gap-2 text-sm text-white mb-2"><Icon size={14} className="text-cyan-300" />{tx(glabel)}</div>
              {gid === "api" && <div className="text-[0.62rem] text-white/40 -mt-1.5 mb-1.5">{tx("Kendi anahtarınızla OpenAI veya Claude seçin")}</div>}
              {ids.map((id) => {
                const x = byId[id];
                const usable = x.configured;
                return (
                  <div key={id} className="flex items-center justify-between gap-2 py-1">
                    <button disabled={!usable || !!choosing} onClick={() => choose(id)} data-testid={`ai-choose-${id}`}
                      aria-pressed={s.active === id} aria-busy={choosing === id}
                      className={`text-left text-xs ${s.active === id ? "text-cyan-200 font-semibold" : usable ? "text-white/75 hover:text-cyan-200" : "text-white/35 cursor-not-allowed"} ${choosing && choosing !== id ? "opacity-50" : ""}`}>
                      {choosing === id ? "⟳ " : s.active === id ? "● " : "○ "}{tx(NAMES[id])}
                    </button>
                    {choosing === id ? <Badge color="cyan">{tx("seçiliyor…")}</Badge> : <StatusBadge s={x} />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-end gap-2 flex-wrap">
          <label className="flex-1 min-w-[200px]">
            <span className="label-eyebrow block mb-1">{tx("Varsayılan model")} · {tx(NAMES[s.active])}</span>
            <select value={active.default_model || "auto"} onChange={(e) => saveDefault(s.active, e.target.value)} data-testid="ai-default-model"
              className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white">
              <option value="auto" className="bg-[#0C1222]">{tx("Otomatik (önerilen)")}</option>
              {active.models.map((m) => <option key={m.id} value={m.id} className="bg-[#0C1222]">{m.label}</option>)}
            </select>
          </label>
          <Btn variant="ghost" onClick={() => test(s.active)} disabled={!active.configured || !!busy} data-testid="ai-test-active">
            {busy === `test-${s.active}` ? tx("Test ediliyor…") : tx("Bağlantıyı test et")}
          </Btn>
        </div>
        {active.last_test?.tested && <p className="text-[0.68rem] text-white/45 mt-1" data-testid="ai-tested">{tx("Test edilen")}: {tx(active.last_test.tested)}</p>}
        {active.last_test && !active.verified && active.last_test.message && <p className="text-[0.7rem] text-red-300 mt-1">{tx(active.last_test.message)}</p>}
      </section>

      {/* ---------------- B. my API connections + AI PC (compact) ---------------- */}
      <section aria-label={tx("Bağlantılar")} className="grid sm:grid-cols-2 gap-2.5">
        {["openai", "anthropic"].map((id) => {
          const x = byId[id];
          return (
            <div key={id} className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid={`ai-key-card-${id}`}>
              <div className="flex items-center justify-between mb-2"><span className="text-sm text-white/85">{tx(NAMES[id])}</span><StatusBadge s={x} /></div>
              {x.key_source === "env" ? (
                <p className="text-[0.68rem] text-white/45">{tx("Anahtar sunucu ortamında tanımlı.")}</p>
              ) : (
                <div className="flex gap-1.5">
                  <input type="password" autoComplete="off" value={keyDraft[id] || ""} placeholder={x.key_source ? tx("Kayıtlı anahtarı değiştir") : tx("API anahtarı")}
                    onChange={(e) => setKeyDraft((k) => ({ ...k, [id]: e.target.value }))} aria-label={`${tx(NAMES[id])} ${tx("API anahtarı")}`}
                    className="flex-1 min-w-0 bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white" data-testid={`ai-key-input-${id}`} />
                  <Btn onClick={() => saveKey(id)} disabled={!keyDraft[id] || !!busy}>{tx("Kaydet")}</Btn>
                  {x.key_source === "saved" && <Btn variant="ghost" onClick={() => removeKey(id)}>{tx("Kaldır")}</Btn>}
                </div>
              )}
              <div className="mt-2 flex gap-2"><Btn variant="ghost" onClick={() => test(id)} disabled={!x.configured || !!busy}>{busy === `test-${id}` ? tx("Test ediliyor…") : tx("Test et")}</Btn></div>
            </div>
          );
        })}
        <div className="rounded-xl border border-white/10 bg-black/20 p-3 sm:col-span-2" data-testid="ai-local-card">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-white/85">{tx("AI PC")}</span><StatusBadge s={byId.local} /></div>
          <div className="flex gap-1.5 flex-wrap">
            <input value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="http://192.168.1.50:1234/v1" aria-label={tx("AI PC uç noktası")}
              className="flex-1 min-w-[220px] bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white font-mono" data-testid="ai-local-endpoint" />
            <Btn onClick={() => saveLocal({ endpoint })}>{tx("Kaydet")}</Btn>
            <Btn variant="ghost" onClick={() => test("local")} disabled={!byId.local.configured || !!busy}>{busy === "test-local" ? tx("Test ediliyor…") : tx("Test et")}</Btn>
          </div>
          <label className="mt-2 flex items-center gap-2 text-[0.72rem] text-white/60">
            <input type="checkbox" checked={!!s.local.tools_enabled} onChange={(e) => saveLocal({ tools_enabled: e.target.checked })} data-testid="ai-local-tools" />
            {tx("Bu model araç çağrısını destekliyor (kayıt arama, ekran açma)")}
          </label>
        </div>
      </section>

      {/* ---------------- C. voice (independent of chat source) ---------------- */}
      <section aria-label={tx("Sesli konuşma")}>
        <div className="label-eyebrow mb-2">{tx("Sesli konuşma")}</div>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label={tx("Sesli konuşma")}>
          {[["live", tx("GPT Live"), Radio, s.voice.live_available], ["standard", tx("Standart ses"), Mic, s.voice.standard_available], ["off", tx("Ses kapalı"), MicOff, true]].map(([id, lbl, Icon, avail]) => (
            <button key={id} role="radio" aria-checked={s.voice.mode === id} onClick={() => setVoice(id)} data-testid={`voice-mode-${id}`}
              className={`rounded-xl border px-3 py-2 text-xs flex items-center gap-2 justify-center ${s.voice.mode === id ? "border-cyan-400/60 bg-cyan-500/15 text-cyan-200" : "border-white/10 bg-black/20 text-white/70"}`}>
              <Icon size={13} />{lbl}{!avail && id !== "off" && <span className="text-amber-300/80">·{tx("kullanılamıyor")}</span>}
            </button>
          ))}
        </div>
        <p className="text-[0.68rem] text-white/45 mt-1.5">{tx("Ses seçimi sohbet kaynağından bağımsızdır; değiştirmek oturum açmaz. \"Kullanılamıyor\" o ses sağlayıcısının hazır olmadığını gösterir; uygulamanın çevrimdışı olduğu anlamına gelmez.")}</p>
      </section>

      {/* ---------------- D. advanced ---------------- */}
      <Advanced testid="ai-voice-advanced">
        {s.sources.map((x) => (
          <div key={x.id}>
            <b className="text-white/70">{tx(NAMES[x.id])}</b>: {tx(x.billing)}.{" "}
            {tx("Görsel")}: {x.capabilities.images ? tx("var") : tx("yok")} · {tx("Araçlar")}: {x.capabilities.tools === "model-dependent" ? tx("modele bağlı") : x.capabilities.tools ? tx("var") : tx("yok")} · {tx("Web araştırması")}: {x.capabilities.web_research ? tx("var") : tx("yok")}
            {x.key_source && <> · {tx("Anahtar kaynağı")}: {x.key_source === "env" ? tx("sunucu ortamı") : tx("ayarlarda kayıtlı")}</>}
          </div>
        ))}
        <div>{tx("AI PC isteği JARVIS sunucusundan gider: 'localhost' bu sunucudur, ayrı AI PC değil. Aynı makine: http://127.0.0.1:1234/v1 (LM Studio) veya :11434/v1 (Ollama). Ayrı PC: yerel ağ IP'si. İzinli portlar")}: {s.local.allowed_ports.join(", ")}. {tx("Port açma, güvenlik duvarı veya yönlendirici değişikliği yapılmaz.")}</div>
        <div>{tx("AI PC seçmek GPT Live, uyandırma sözcüğü, çevrimdışı ses tanıma/okuma veya web araştırması sağlamaz; bunlar ayrı kaynaklardır.")}</div>
        <div>{tx("ChatGPT Plus veya Claude Pro aboneliği API anahtarı değildir; burada yalnızca sağlayıcı API anahtarı kullanılır.")}</div>
        <label className="flex items-center gap-2">
          {tx("Kaynak çalışmazsa önerilecek yedek")}:
          <select value={s.fallback_provider || ""} onChange={(e) => setFallback(e.target.value)} data-testid="ai-fallback"
            className="bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-white">
            <option value="" className="bg-[#0C1222]">{tx("Yok (sor)")}</option>
            {s.sources.filter((x) => x.configured && x.id !== s.active).map((x) => <option key={x.id} value={x.id} className="bg-[#0C1222]">{tx(NAMES[x.id])}</option>)}
          </select>
        </label>
        <div>{tx("Yedek kaynak asla otomatik kullanılmaz; hata olduğunda yalnızca düğme olarak önerilir.")}</div>
      </Advanced>
    </div>
  );
};

export default AiVoiceSettings;
